import { computed, inject, Injectable, signal } from '@angular/core';
import { NotificationDto } from '@bannerflow/types.notification-service';
import { Subject } from 'rxjs';
import {
  ImageExportNotification,
  NotificationReceivedEvent,
  VideoExportNotification
} from '../domain';
import { FilterType } from '../domain/states.types';
import {
  downloadLink,
  isImageExportNotification,
  isVideoExportNotification
} from '../utils';
import { NSBrowserTabService } from './browser-tab.service';
import { NotificationDataService } from './notification.data.service';

@Injectable()
export class NotificationService {
  private browserTabService = inject(NSBrowserTabService);
  private notificationsDataService = inject(NotificationDataService);

  private _notifications = signal<NotificationDto[]>([]);
  private _numberOfUnread = signal(0);
  private currentPage = signal(1);
  private filter = signal<FilterType>('showAll');
  private itemsPerPage = signal(25);
  private totalNumberOfItems = signal(0);

  canLoadMore = computed(() => this.computeCanLoadMore());
  currentFilter = computed(() => this.filter());
  error = signal<unknown | undefined>(undefined);
  hasUnreadNotifications = computed(() => this.computeHasUnreadNotifications());
  loading = signal(false);
  notifications = computed(() => this.computeNotifications());
  numberOfUnread = this._numberOfUnread.asReadonly();

  private _newNotification$ = new Subject<NotificationDto>();
  newNotification$ = this._newNotification$.asObservable();

  setFilter(newFilter: FilterType): void {
    this.filter.set(newFilter);
  }

  loadNumberOfUnreadNotifications(): void {
    this.notificationsDataService.getNumberUnreadNotifications().subscribe({
      next: ({ count }) => {
        this.error.set(undefined);
        this._numberOfUnread.set(count);
      },
      error: error => {
        this.error.set(error);
      }
    });
  }

  loadMoreNotifications(): void {
    this.loading.set(true);

    const currentPage = this.currentPage();
    const itemsPerPage = this.itemsPerPage();

    this.notificationsDataService
      .loadMostRecentNotification(currentPage, itemsPerPage)
      .subscribe({
        next: ({ items, paginationMetadata }) => {
          this.currentPage.set(paginationMetadata.currentPage);
          this.totalNumberOfItems.set(paginationMetadata.totalItems);
          this._notifications.update(notifications => [
            ...notifications,
            ...(items ?? [])
          ]);
          this.loading.set(false);
          this.error.set(undefined);
        },
        error: error => {
          this.loading.set(false);
          this.error.set(error);
        }
      });
  }

  prependNotification(notification: NotificationDto): void {
    this._newNotification$.next(notification);
    this._notifications.update(notifications => [
      notification,
      ...notifications.filter(({ id }) => id !== notification.id)
    ]);
  }

  loadAndPrependNotification(
    newNotificationEvent: NotificationReceivedEvent
  ): void {
    this.loading.set(true);
    this.notificationsDataService
      .getNotificationById(newNotificationEvent.notificationId)
      .subscribe({
        next: newNotification => {
          this._newNotification$.next(newNotification);
          this._notifications.update(notifications => [
            newNotification,
            ...notifications.filter(({ id }) => id !== newNotification.id)
          ]);
          this.loading.set(false);
          this.error.set(undefined);

          if (
            !newNotification.read &&
            (isVideoExportNotification(newNotification) ||
              isImageExportNotification(newNotification))
          ) {
            this.autoDownload(newNotification);
          }
        },
        error: error => {
          this.loading.set(false);
          this.error.set(error);
        }
      });
  }

  refreshNotifications(): void {
    this.currentPage.set(1);
    this._notifications.set([]);
    this.filter.set('showAll');
    this.loadMoreNotifications();
  }

  markAsRead(notification: NotificationDto): void {
    this.notificationsDataService.markAsRead(notification).subscribe({
      next: () => {
        this._notifications.update(notifications =>
          notifications.map(currentNotification =>
            currentNotification.id === notification.id
              ? { ...currentNotification, read: true }
              : currentNotification
          )
        );
        this._numberOfUnread.update(numberOfUnread =>
          Math.max(0, numberOfUnread - 1)
        );
        this.error.set(undefined);
      },
      error: error => {
        this.error.set(error);
      }
    });
  }

  markAllAsRead(): void {
    this.notificationsDataService.markAllAsRead().subscribe({
      next: () => {
        this._notifications.update(notifications =>
          notifications.map(currentNotification => ({
            ...currentNotification,
            read: true
          }))
        );
        this._numberOfUnread.set(0);
        this.error.set(undefined);
      },
      error: error => {
        this.error.set(error);
      }
    });
  }

  private computeNotifications(): NotificationDto[] {
    const filter = this.filter();
    const notifications = this._notifications();
    if (filter === 'showUnread') {
      return notifications.filter(({ read }) => !read);
    }
    return notifications;
  }

  private computeCanLoadMore(): boolean {
    const currentPage = this.currentPage();
    const totalNumberOfItems = this.totalNumberOfItems();
    const itemsPerPage = this.itemsPerPage();
    return currentPage * itemsPerPage < totalNumberOfItems;
  }

  private computeHasUnreadNotifications(): boolean {
    const notifications = this._notifications();
    return notifications.some(({ read }) => !read);
  }

  private autoDownload(
    notification: VideoExportNotification | ImageExportNotification
  ): void {
    if (!this.browserTabService.isLastTabOpened()) {
      // Only auto-download in the most recently focused tab
      return;
    }
    if (notification.content.link) {
      downloadLink(notification.content.link);
      this.markAsRead(notification);
    }
  }
}
